import { useCallback, useEffect, useRef, useState } from 'react';
import { EvalApiService } from '../services/EvalApiService';
import { EvalGtSample } from '../types';

export const GT_SAMPLES_PAGE_SIZE = 100;

export type GtSampleFilters = {
  dataset_id?: string;
  unassigned?: boolean;
  label_source?: string;
  store_id?: number;
  chain_id?: number;
  ingested_by?: string;
};

/**
 * Paged GT-sample list with incremental "load more" semantics.
 * Resets to the first page whenever the filters change.
 */
export function usePagedGtSamples(token: string, filters: GtSampleFilters, enabled = true) {
  const [samples, setSamples] = useState<EvalGtSample[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // Guards against out-of-order responses when filters change quickly.
  const requestIdRef = useRef(0);

  // Callers pass a fresh filters object every render — depend on its content
  // (filtersKey) and read the latest value through a ref.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const filtersKey = JSON.stringify(filters);

  const load = useCallback(
    async (skip: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const { items, total: t } = await EvalApiService.listGtSamples(token, {
          ...filtersRef.current,
          skip,
          limit: GT_SAMPLES_PAGE_SIZE,
        });
        if (requestId !== requestIdRef.current) return;
        setTotal(t);
        setSamples((prev) => (append ? [...prev, ...items] : items));
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [token, filtersKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!enabled) return;
    load(0, false);
  }, [load, enabled]);

  const loadMore = useCallback(() => load(samples.length, true), [load, samples.length]);
  const reload = useCallback(() => load(0, false), [load]);

  return { samples, total, loading, hasMore: samples.length < total, loadMore, reload };
}
